import { type Permission, type Reservation, ReservationStatus } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import { useMemo } from 'react';

import { createCheckoutPermissions } from './use-checkout-permissions.hook';
import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createSpotcheckPermissions } from './use-spotcheck-permissions.hook';

interface FeatureFlags {
	allowPastBookings?: boolean;
}

type UseReservationPermissionsProps = PermissionsProps & FeatureFlags;

const MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST = { hours: 1 };

export const createReservationPermissions = (
	permissions: ReturnType<typeof usePermissions>,
	featureFlags?: FeatureFlags
) => {
	const { allowPastBookings = false } = featureFlags ?? {};
	const {
		hasAnyPermission: _hasAnyPermission,
		useReservations,
		useReservationsClose,
		useEquipmentPicking,
		usePastReservations,
		useBlockEditOfOpenReservations,
		usePdf,
		useSpotchecks,
		canUpgrade,
	} = permissions;

	const checkoutPermissions = createCheckoutPermissions(permissions);
	const spotcheckPermissions = createSpotcheckPermissions(permissions);

	const hasAnyPermission = (...required: Permission[]) => useReservations && _hasAnyPermission(...required);

	const canWriter = (own?: boolean) =>
		hasAnyPermission('RESERVATIONS_WRITER', 'RESERVATIONS_WRITER_RESTRICTED') ||
		(own && hasAnyPermission('RESERVATIONS_OWN_WRITER'));

	const canArchive = (own?: boolean) => {
		return (
			hasAnyPermission('RESERVATIONS_ARCHIVER', 'RESERVATIONS_ARCHIVER_RESTRICTED') ||
			(own && hasAnyPermission('RESERVATIONS_OWN_ARCHIVER'))
		);
	};

	const canLabel = (own?: boolean) => {
		return (
			hasAnyPermission('RESERVATIONS_LABELER', 'RESERVATIONS_LABELER_RESTRICTED') ||
			(own && hasAnyPermission('RESERVATIONS_OWN_LABELER'))
		);
	};

	const reservationPermissions = {
		canRead: (own?: boolean) => {
			return (
				hasAnyPermission('RESERVATIONS_READER', 'RESERVATIONS_READER_RESTRICTED') ||
				(own && hasAnyPermission('RESERVATIONS_OWN_READER'))
			);
		},
		canCreate: () => canWriter(true),
		canReserve: canWriter,
		// Picking who a reservation is for (`setCustomer`): the own tier never covers somebody else.
		canReserveForSomeoneElse: (): boolean => !!canWriter(false),
		canUpdate: canWriter,
		canOverrideConflicts: () => hasAnyPermission('RESERVATIONS_CONFLICT_CREATOR'),
		canClose: (reservation?: Pick<Reservation, 'status'>, own?: boolean) => {
			if (!useReservationsClose) return false;
			if (reservation && reservation.status !== ReservationStatus.OPEN) return false;

			return (
				hasAnyPermission('RESERVATIONS_CLOSER', 'RESERVATIONS_CLOSER_RESTRICTED') ||
				(own && hasAnyPermission('RESERVATIONS_OWN_CLOSER'))
			);
		},
		canProceedToPickup: (reservation: Pick<Reservation, 'fromDate' | 'toDate' | 'status'>, own?: boolean) => {
			if (!useEquipmentPicking) return false;
			return reservationPermissions.canConvertToCheckout(reservation, own);
		},
		canConvertToCheckout: (reservation: Pick<Reservation, 'fromDate' | 'toDate' | 'status'>, own?: boolean) => {
			if (!checkoutPermissions.canCheckout(own)) return false;
			if (!reservation?.fromDate || !reservation.toDate) return false;
			if (reservation.status !== ReservationStatus.OPEN) return false;
			try {
				const now = DateTime.now();
				const isInPast =
					DateTime.fromISOString(reservation.fromDate).isBefore(now) &&
					DateTime.fromISOString(reservation.toDate).isBefore(now);
				return !isInPast;
			} catch (error) {
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line no-console
					console.error('[canConvertToCheckout] DateTime operation failed:', error);
				}
				return false;
			}
		},
		canReadCheckouts: (own?: boolean) => {
			return checkoutPermissions.canRead(own);
		},
		canWritePast: (own?: boolean) => {
			if (!usePastReservations || !allowPastBookings) return false;
			return hasAnyPermission('RESERVATIONS_WRITER_PAST') && canWriter(own);
		},
		canUndoFutureBooking: (reservation: Pick<Reservation, 'status'>, own?: boolean) => {
			if (reservation.status !== ReservationStatus.OPEN) return false;
			return canWriter(own);
		},
		canUndoPastBooking: (reservation: Pick<Reservation, 'status'>, own?: boolean) => {
			const canUndoBooking = reservationPermissions.canUndoFutureBooking(reservation, own);
			return canUndoBooking && reservationPermissions.canWritePast(own);
		},
		canUndoCurrentBooking: (reservation: Pick<Reservation, 'status' | 'fromDate'>, own?: boolean) => {
			if (reservation.status !== ReservationStatus.OPEN) return false;
			// Check if user has permission to undo bookings
			if (!canWriter(own)) return false;

			// If booking hasn't started yet (not in past), can always undo
			const isFromDateInPast =
				reservation.fromDate &&
				DateTime.fromISOString(reservation.fromDate).isBefore(
					DateTime.now().subtract(MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST)
				);
			if (!isFromDateInPast) {
				return true;
			}
			// For bookings that have already started (in past):
			// If past bookings are disabled OR blocking is disabled, always allow undo
			const usePastBookings = !!usePastReservations;
			const blockEditOfOpenBookings = !!useBlockEditOfOpenReservations;
			const alwaysAllowUndo = !usePastBookings || !blockEditOfOpenBookings;
			if (alwaysAllowUndo) {
				return true;
			}
			// If past bookings are enabled AND blocking is enabled:
			// Need write-past permission to undo past bookings
			return reservationPermissions.canWritePast(own);
		},
		canEdit: (reservation: Pick<Reservation, 'status'>, own?: boolean) => {
			if (reservation.status !== ReservationStatus.OPEN) return false;
			return canWriter(own);
		},
		canEditFields: (reservation: Pick<Reservation, 'archived'>, own?: boolean) => {
			if (reservation.archived) return false;
			return canWriter(own);
		},
		canDelete: (reservation: Pick<Reservation, 'status'>, own?: boolean) => {
			if (reservation.status !== ReservationStatus.CREATING) return false;
			return canWriter(own);
		},
		canCancel: (reservation: Pick<Reservation, 'status'>, own?: boolean) => {
			if (reservation.status !== ReservationStatus.OPEN) return false;
			return (
				hasAnyPermission('RESERVATIONS_CANCELER', 'RESERVATIONS_CANCELER_RESTRICTED') ||
				(own && hasAnyPermission('RESERVATIONS_OWN_CANCELER'))
			);
		},
		canBookAgain: canWriter,
		canRepeatBooking: canWriter,
		canArchive: (reservation: Pick<Reservation, 'status' | 'archived'>, own?: boolean) => {
			if (![ReservationStatus.CLOSED, ReservationStatus.CANCELLED].includes(reservation.status)) return false;
			if (reservation.archived) return false;
			return canArchive(own);
		},
		canUndoArchive: (reservation: Pick<Reservation, 'archived'>, own?: boolean) => {
			if (!reservation.archived) return false;
			return canArchive(own);
		},
		canGenerateDocument: (own?: boolean) => {
			if (!usePdf) return false;
			return (
				hasAnyPermission('RESERVATIONS_DOCUMENT_GENERATOR', 'RESERVATIONS_DOCUMENT_GENERATOR_RESTRICTED') ||
				(own && hasAnyPermission('RESERVATIONS_OWN_DOCUMENT_GENERATOR'))
			);
		},
		canUpgradePlan: canUpgrade,
		canCreateSpotcheck: () => {
			if (!useSpotchecks) return false;
			return spotcheckPermissions.canCreate();
		},
		canReadBooking: (own?: boolean) => reservationPermissions.canRead(own),
		canSetLabel: (reservation: Pick<Reservation, 'archived'>, own?: boolean) => {
			if (reservation.archived) return false;
			return canLabel(own);
		},
		canClearLabel: (reservation: Pick<Reservation, 'archived'>, own?: boolean) => {
			if (reservation.archived) return false;
			return canLabel(own);
		},
	};

	return reservationPermissions;
};

export const useReservationPermissions = ({ allowPastBookings = false, ...props }: UseReservationPermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(
		() => createReservationPermissions(permissions, { allowPastBookings }),
		[permissions, allowPastBookings]
	);
};
